import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Filter, ShoppingCart, Leaf, HelpCircle, Minus, Plus, RefreshCw, Package, Clock, Truck, CalendarCheck, CheckCircle2, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import axios from "axios";
import { API, useCart, useAuth } from "@/App";
import { toast } from "sonner";
import { LoginPromptDialog } from "@/components/LoginPromptDialog";
import { MicrogreenQuiz } from "@/components/MicrogreenQuiz";
import { SEO } from "@/components/SEO";

const categories = [
  { value: "all", label: "All Products" },
  { value: "popular", label: "Popular" },
  { value: "herbs", label: "Herbs" },
  { value: "specialty", label: "Specialty" },
  { value: "wellness", label: "Wellness" }
];

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [faqs, setFaqs] = useState([]);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [subDialog, setSubDialog] = useState(null); // unused, kept for safety
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
        setFilteredProducts(response.data);
      } catch (error) {
        console.error("Failed to fetch products:", error);
        toast.error("Failed to load products");
      } finally {
        setLoading(false);
      }
    };
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/faq`);
        setFaqs(response.data);
      } catch (error) {
        console.error("Failed to fetch FAQs:", error);
      }
    };
    fetchProducts();
    fetchFaqs();
  }, []);

  useEffect(() => {
    let filtered = products;
    if (selectedCategory !== "all") {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        p =>
          p.name.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query)
      );
    }
    setFilteredProducts(filtered);
  }, [searchQuery, selectedCategory, products]);

  const handleAddToCart = async (productId, e) => {
    e.preventDefault();
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    try {
      await addToCart(productId, 1);
      setQuantities(prev => ({ ...prev, [productId]: 1 }));
      toast.success("Added to cart!");
    } catch (error) {
      toast.error("Failed to add to cart");
    }
  };

  const handleSubscribe = (plan) => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    navigate("/checkout", { state: { subscription: { plan, price: plan === "A" ? 720 : 440, weeklyPrice: plan === "A" ? 180 : 110, quantity: plan === "A" ? "50g" : "25g" } } });
  };

  const handleSubscribeProduct = null; // removed

  const handleQtyChange = async (productId, delta, e) => {
    e.preventDefault();
    const current = quantities[productId] || 0;
    const newQty = current + delta;
    if (newQty <= 0) {
      setQuantities(prev => { const n = { ...prev }; delete n[productId]; return n; });
      toast.info("Removed from cart");
      return;
    }
    try {
      await addToCart(productId, delta);
      setQuantities(prev => ({ ...prev, [productId]: newQty }));
    } catch (error) {
      toast.error("Failed to update cart");
    }
  };

  return (
    <div className="pt-16 md:pt-20 lg:pt-24" data-testid="products-page">
      <SEO 
        title="Fresh Microgreens" 
        description="Browse our collection of organic, nutrient-dense microgreens. Broccoli, Radish, Sunflower, Pea Shoots and more. Delivered fresh to your door."
        keywords="buy microgreens, organic microgreens, broccoli microgreens, radish microgreens, fresh greens"
        path="/products"
      />
      {/* Compact Hero + Filters */}
      <section className="py-8 md:py-10 bg-praroha-forest" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            {/* Left - Title */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-2 mb-2">
                <Leaf className="w-5 h-5 text-praroha-sage" />
                <span className="text-praroha-sage text-sm font-medium">Our Products</span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
                Fresh Microgreens
              </h1>
              <p className="text-white/50 text-sm mt-1">
                Nutrient-dense microgreens, harvested fresh and delivered to your door.
              </p>
            </motion.div>
            {/* Right - Filters */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center"
            >
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 w-full sm:w-56 focus:bg-white/15"
                  data-testid="product-search-input"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white w-full sm:w-44" data-testid="category-filter">
                  <Filter className="w-4 h-4 mr-2 text-white/50" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-white/40 whitespace-nowrap hidden sm:block">
                {filteredProducts.length} items
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Scrolling Delivery Notice */}
      <div className="bg-praroha-terracotta/90 py-2.5 overflow-hidden" data-testid="delivery-notice-banner">
        <div className="animate-marquee whitespace-nowrap flex items-center gap-12">
          {[...Array(6)].map((_, i) => (
            <span key={i} className="inline-flex items-center gap-2 text-white font-medium text-sm tracking-wide">
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Kindly Note: Delivery of products will take approximately 7 to 10 days from the date of ordering.
            </span>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      <section className="py-8 md:py-10 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-80 bg-muted animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground">No products found matching your criteria.</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("all");
                }}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
              {filteredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.04 }}
                  className="flex"
                >
                  <Card
                    className="group flex flex-col w-full bg-white overflow-hidden border-border/40 hover:border-praroha-sage/30 hover:shadow-hover transition-all duration-300"
                    data-testid={`product-card-${product.slug}`}
                  >
                    <Link to={`/products/${product.slug}`}>
                      <div className="aspect-[4/3] overflow-hidden bg-praroha-cream/50">
                        <img
                          src={product.image}
                          alt={`${product.name} - Fresh Organic Microgreens`}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    </Link>
                    <CardContent className="p-3 sm:p-4 flex flex-col flex-1">
                      <div className="flex items-start justify-between gap-1 sm:gap-2 mb-1">
                        <Link to={`/products/${product.slug}`}>
                          <h3 className="font-heading text-sm sm:text-base font-semibold text-praroha-forest group-hover:text-praroha-sage transition-colors line-clamp-2">
                            {product.name}
                          </h3>
                        </Link>
                        <span className="text-[10px] font-medium text-praroha-sage bg-praroha-sage/10 px-1.5 sm:px-2 py-0.5 rounded-full capitalize shrink-0 hidden sm:inline">
                          {product.category}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2 sm:mb-3 line-clamp-2 flex-1">
                        {product.short_description}
                      </p>
                      <div className="flex items-center justify-between mt-auto gap-2">
                        <div className="shrink-0">
                            <span className="font-semibold text-praroha-forest text-sm">
                              ₹{product.price} <span className="text-xs font-normal text-muted-foreground">/ {product.weight || '50g'}</span>
                            </span>
                        </div>
                        {product.in_stock ? (
                          quantities[product.id] ? (
                            <div
                              className="inline-flex items-center bg-praroha-forest rounded-full shadow-md shadow-praroha-forest/20"
                              data-testid={`qty-stepper-${product.slug}`}
                            >
                              <button
                                className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white active:scale-90 transition-all"
                                onClick={(e) => handleQtyChange(product.id, -1, e)}
                                data-testid={`qty-minus-${product.slug}`}
                              >
                                <Minus className="w-3.5 h-3.5" strokeWidth={2.5} />
                              </button>
                              <span
                                className="w-7 text-center text-sm font-bold text-white tabular-nums"
                                data-testid={`qty-value-${product.slug}`}
                              >
                                {quantities[product.id]}
                              </span>
                              <button
                                className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white active:scale-90 transition-all"
                                onClick={(e) => handleQtyChange(product.id, 1, e)}
                                data-testid={`qty-plus-${product.slug}`}
                              >
                                <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                              </button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              className="bg-praroha-terracotta hover:bg-praroha-terracotta/90 text-white rounded-full text-xs px-4 h-8"
                              onClick={(e) => handleAddToCart(product.id, e)}
                              data-testid={`add-to-cart-${product.slug}`}
                            >
                              <ShoppingCart className="w-3.5 h-3.5 mr-1" />
                              Add
                            </Button>
                          )
                        ) : (
                          <Button size="sm" disabled className="rounded-full text-xs px-3 h-8">Sold Out</Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Subscription Model */}
      <section className="py-14 md:py-20 bg-praroha-forest relative overflow-hidden" data-testid="subscription-model-section">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 text-white text-sm font-medium rounded-full mb-4">
              <RefreshCw className="w-4 h-4" />
              Subscription Model
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-3">
              Fresh Greens, Every Week
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto text-sm md:text-base">
              We follow a flexible, customer-first subscription model designed to deliver fresh microgreens at your convenience.
            </p>
            <p className="text-amber-300 text-sm font-medium mt-3 max-w-xl mx-auto">
              Every week, a different variety of microgreens will be delivered — keeping your meals fresh, diverse, and nutrient-rich!
            </p>
          </motion.div>

          {/* Plans */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Plan A */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 h-full hover:bg-white/15 transition-all" data-testid="plan-a-card">
                <CardContent className="p-6 md:p-8 flex flex-col h-full">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                      <Package className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-white">Plan A</h3>
                      <p className="text-white/70 text-xs">Monthly Subscription</p>
                    </div>
                  </div>
                  <div className="mb-5">
                    <div className="flex items-baseline gap-1">
                      <span className="font-heading text-4xl font-bold text-white">&#8377;720</span>
                      <span className="text-white/70 text-sm">/ month</span>
                    </div>
                    <p className="text-amber-300 text-sm font-medium mt-1">&#8377;180 per week</p>
                  </div>
                  <ul className="space-y-3 mb-6 flex-1">
                    <li className="flex items-start gap-2 text-white text-sm">
                      <CheckCircle2 className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                      <span>4 boxes per month (1 per week)</span>
                    </li>
                    <li className="flex items-start gap-2 text-white text-sm">
                      <CheckCircle2 className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                      <span>50g per box</span>
                    </li>
                    <li className="flex items-start gap-2 text-white text-sm">
                      <CheckCircle2 className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                      <span>Option for additional boxes at same rate</span>
                    </li>
                  </ul>
                  <Button
                    onClick={() => handleSubscribe("A")}
                    className="w-full bg-praroha-sage hover:bg-praroha-sage/90 text-white rounded-full py-5"
                    data-testid="subscribe-plan-a-btn"
                  >
                    Subscribe Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Plan B */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 h-full hover:bg-white/15 transition-all" data-testid="plan-b-card">
                <CardContent className="p-6 md:p-8 flex flex-col h-full">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                      <Package className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-white">Plan B</h3>
                      <p className="text-white/70 text-xs">Monthly Subscription</p>
                    </div>
                  </div>
                  <div className="mb-5">
                    <div className="flex items-baseline gap-1">
                      <span className="font-heading text-4xl font-bold text-white">&#8377;440</span>
                      <span className="text-white/70 text-sm">/ month</span>
                    </div>
                    <p className="text-amber-300 text-sm font-medium mt-1">&#8377;110 per week</p>
                  </div>
                  <ul className="space-y-3 mb-6 flex-1">
                    <li className="flex items-start gap-2 text-white text-sm">
                      <CheckCircle2 className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                      <span>4 boxes per month (1 per week)</span>
                    </li>
                    <li className="flex items-start gap-2 text-white text-sm">
                      <CheckCircle2 className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                      <span>25g per box</span>
                    </li>
                    <li className="flex items-start gap-2 text-white text-sm">
                      <CheckCircle2 className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                      <span>Option for additional boxes at same rate</span>
                    </li>
                  </ul>
                  <Button
                    onClick={() => handleSubscribe("B")}
                    className="w-full bg-praroha-sage hover:bg-praroha-sage/90 text-white rounded-full py-5"
                    data-testid="subscribe-plan-b-btn"
                  >
                    Subscribe Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Custom Subscription */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="md:col-span-2 lg:col-span-1"
            >
              <Card className="bg-white/5 backdrop-blur-sm border-white/10 border-dashed h-full hover:bg-white/10 transition-all" data-testid="custom-plan-card">
                <CardContent className="p-6 md:p-8 flex flex-col h-full">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <Truck className="w-5 h-5 text-white/80" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-white">Custom Plan</h3>
                      <p className="text-white/70 text-xs">For Businesses</p>
                    </div>
                  </div>
                  <p className="text-white/80 text-sm mb-5">
                    Tailored for cafes, restaurants, fitness centres, and bulk consumers. Get a custom plan that fits your needs.
                  </p>
                  <ul className="space-y-3 mb-6 flex-1">
                    <li className="flex items-start gap-2 text-white text-sm">
                      <CheckCircle2 className="w-4 h-4 text-white/60 shrink-0 mt-0.5" />
                      <span>Custom quantities &amp; frequencies</span>
                    </li>
                    <li className="flex items-start gap-2 text-white text-sm">
                      <CheckCircle2 className="w-4 h-4 text-white/60 shrink-0 mt-0.5" />
                      <span>Bulk pricing available</span>
                    </li>
                    <li className="flex items-start gap-2 text-white text-sm">
                      <CheckCircle2 className="w-4 h-4 text-white/60 shrink-0 mt-0.5" />
                      <span>Dedicated account manager</span>
                    </li>
                  </ul>
                  <Link to="/contact" data-testid="custom-plan-contact-btn">
                    <Button
                      variant="outline"
                      className="w-full border-white/30 text-white hover:bg-white/10 rounded-full py-5"
                    >
                      Contact Us
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: Clock, title: "Harvested Fresh", desc: "All microgreens harvested within 6 hours before delivery" },
              { icon: Truck, title: "Doorstep Delivery", desc: "Delivered in eco-friendly, food-safe packaging" },
              { icon: CalendarCheck, title: "Modify Anytime", desc: "Pause, resume, or modify your subscription anytime" },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10"
                data-testid={`sub-highlight-${i}`}
              >
                <item.icon className="w-5 h-5 text-praroha-sage shrink-0" />
                <div>
                  <p className="text-white text-sm font-medium">{item.title}</p>
                  <p className="text-white/70 text-xs">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Find Your Microgreen Quiz */}
      <MicrogreenQuiz products={products} />

      {/* How To Consume Microgreens */}
      <section className="py-12 md:py-16 bg-background" data-testid="how-to-consume-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="inline-block px-4 py-2 bg-praroha-sage/10 text-praroha-sage text-sm font-medium rounded-full mb-4">
              Usage Guide
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest mb-3">
              How To Consume Microgreens
            </h2>
            <p className="text-muted-foreground">
              Maximizing Flavour and Nutrition
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Consumption Methods */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="group h-full bg-white overflow-hidden border-border/50 hover:border-praroha-sage/30 hover:shadow-hover transition-all duration-300">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1523218475566-ea0db0c23067?auto=format&fit=crop&q=80&w=600"
                    alt="Microgreens garnished dish"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-praroha-forest mb-2">
                    Consumption Methods
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Microgreens can be consumed raw, blended in smoothies or used as gourmet garnishes, enhancing flavour and nutrition in any meal.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Culinary Uses */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="group h-full bg-white overflow-hidden border-border/50 hover:border-praroha-sage/30 hover:shadow-hover transition-all duration-300">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://static.prod-images.emergentagent.com/jobs/cf48c9a0-b028-44b3-900f-42fac6806232/images/a85ab1ee6754671ffe48a8714da027b557a73bce3ddcd3fed2d3f08104ccd6cb.png"
                    alt="Fresh microgreens for culinary use"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-praroha-forest mb-2">
                    Culinary Uses
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Commonly used in salads, sandwiches and toppings on entrees. Microgreens elevate both the taste and visual appeal of dishes.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Shelf Life */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="group h-full bg-white overflow-hidden border-border/50 hover:border-praroha-sage/30 hover:shadow-hover transition-all duration-300">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1647613233075-e0d5546b0f22?auto=format&fit=crop&q=80&w=600"
                    alt="Microgreens shelf life storage"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-praroha-forest mb-2">
                    Shelf Life
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    With proper handling, microgreens can last up to two weeks in refrigeration maintaining their flavour and nutritional integrity.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product FAQs */}
      {faqs.length > 0 && (
        <section className="py-12 md:py-16 bg-praroha-cream/40" data-testid="products-faq-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-praroha-forest/10 text-praroha-forest text-sm font-medium rounded-full mb-4">
                <HelpCircle className="w-4 h-4" />
                FAQs
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest mb-3">
                Frequently Asked Questions
              </h2>
              <p className="text-muted-foreground">
                Everything you need to know about our microgreens
              </p>
            </motion.div>

            <Accordion type="single" collapsible className="space-y-3" data-testid="products-faq-accordion">
              {faqs.map((faq, index) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <AccordionItem
                    value={faq.id}
                    className="bg-white rounded-xl border border-border/50 px-6 data-[state=open]:shadow-soft"
                    data-testid={`product-faq-item-${index}`}
                  >
                    <AccordionTrigger className="text-left py-5 hover:no-underline">
                      <span className="font-heading text-base md:text-lg font-medium text-praroha-forest pr-4">
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-5">
                      <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
                        {faq.answer}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mt-8"
            >
              <Link to="/faq" data-testid="view-all-faqs-link">
                <Button
                  variant="outline"
                  className="rounded-full border-praroha-forest/30 text-praroha-forest hover:bg-praroha-forest hover:text-white transition-colors"
                >
                  View All FAQs
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      )}
      <LoginPromptDialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt} />
    </div>
  );
}
